import { DateOrTimeView } from "@mui/x-date-pickers";
import { Article, Movement } from "types";

export const STATUS_CODES = {
  OK: 200,
  ACCEPTED: 202,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
};

export const USER_STORAGE_KEY = "shc-user";

export const STOCK_OBSERVATION_SUMMARY_EXPORT_BATCH_SIZE = 500;

export const BATCH_POLLING_INTERVAL_MS = 3000;

export const FILE_UPLOAD_MAX_SIZE_BYTES = 10 * 1024 * 1024;

export const FILE_IMAGE_CONTENT_TYPES = ["image/jpeg", "image/png"] as const;

export const FILE_UPLOAD_CONTENT_TYPES = [
  "application/pdf",
  ...FILE_IMAGE_CONTENT_TYPES,
] as const;

export const WORK_ORDER_STATUS = {
  PENDING: "pending",
  IN_PROCESS: "in_process",
  FINISHED: "finished",
  CONFIRMED: "confirmed",
} as const;

export const WORK_ORDER_BUNDLE_STATUS = {
  PENDING: "pending",
  IN_PROCESS: "in_process",
  COMPLETED: "completed",
} as const;

export const INCOME_ENTRY_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  VERIFIED: "verified",
  FINISHED: "finished",
} as const;

export const ROUTES = {
  INDEX: "/",
  LOGIN: "/login",
  REGISTER: "/register",
  HOME: "/home",
  ORDERS: "/orders",
  ORDERS_DETAIL: "/orders/:id",
  INCOME_WITH_DRAFT: "/income/:movement_draft_id",
  INCOME: "/income",
  FRACTIONATION: "/fractionation",
  FRACTIONATION_DETAIL: "/fractionation/:id",
  INCOME_ENTRY: "/income-entry",
  INCOME_ENTRY_DETAIL: "/income-entry/:id",
  INCOME_ENTRY_DETAIL_REVIEW: "/income-entry/:id/review",
  DISCHARGE: "/discharges",
  DISCHARGE_WITH_DRAFT: "/discharges/:movement_draft_id",
  PREPICKING: "/prepicking",
  ADDITIONALS: "/additionals",
  TRANSFER: "/transfer",
  OPERATIONS: "/operations",
  HISTORY: "/history",
  SALES: "/sales",
  SALES_DETAIL: "/sales/:id",
  LISTS: "/lists",
  CLIENTS: "/lists/clients",
  ASSOCIADED_CLIENTS: "/lists/associated-clients",
  PRODUCTS: "/lists/products",
  STORES: "/lists/stores",
  LOCATIONS: "/lists/locations",
  SUBLOCATIONS: "/lists/sublocations",
  ADDITIONAL_ITEMS: "/lists/additional-items",
  SETTLEMENT_TYPES: "/lists/settlement-types",
  UNITS: "/lists/units",
  CONTAINERS: "/lists/containers",
  USER_SETTINGS: "/user-settings",
  USERS: "/lists/users",
  CONFIGS: "/configs",
  CHANGE_PASSWORD: "/change-password",
  GROUPS: "/lists/groups",
};

export enum Roles {
  ADMIN = "ADMIN",
  SUPERVISOR = "SUPERVISOR",
  OPERATOR = "OPERATOR",
  CLIENT = "CLIENT",
  SUPERADMIN = "SUPERADMIN",
}

export enum LocationTypes {
  PISO = "PISO",
  RACK = "RACK",
}

export const ROUTES_PERMISSIONS: Record<string, string[]> = {
  [ROUTES.HOME]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
    Roles.OPERATOR,
    Roles.CLIENT,
  ],
  [ROUTES.ORDERS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
    Roles.OPERATOR,
  ],
  [ROUTES.ORDERS_DETAIL]: [Roles.OPERATOR],
  [ROUTES.INCOME]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.INCOME_WITH_DRAFT]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.FRACTIONATION]: [Roles.OPERATOR],
  [ROUTES.FRACTIONATION_DETAIL]: [Roles.OPERATOR],
  [ROUTES.INCOME_ENTRY]: [
    Roles.OPERATOR,
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.INCOME_ENTRY_DETAIL]: [
    Roles.OPERATOR,
  ],
  [ROUTES.INCOME_ENTRY_DETAIL_REVIEW]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.DISCHARGE_WITH_DRAFT]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.DISCHARGE]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.PREPICKING]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.ADDITIONALS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.TRANSFER]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.OPERATIONS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
    Roles.CLIENT,
  ],
  [ROUTES.HISTORY]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.SALES]: [Roles.SUPERADMIN],
  [ROUTES.SALES_DETAIL]: [Roles.SUPERADMIN],
  [ROUTES.LISTS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.CLIENTS]: [
    Roles.SUPERADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.ASSOCIADED_CLIENTS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.PRODUCTS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.STORES]: [
    Roles.SUPERADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.LOCATIONS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.SUBLOCATIONS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.UNITS]: [
    Roles.SUPERADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.CONTAINERS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.ADDITIONAL_ITEMS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.GROUPS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.SETTLEMENT_TYPES]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
  ],
  [ROUTES.USERS]: [Roles.SUPERADMIN],
  [ROUTES.USER_SETTINGS]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
    Roles.OPERATOR,
  ],
  [ROUTES.CONFIGS]: [Roles.SUPERADMIN, Roles.ADMIN, Roles.SUPERVISOR],
  [ROUTES.CHANGE_PASSWORD]: [
    Roles.SUPERADMIN,
    Roles.ADMIN,
    Roles.SUPERVISOR,
    Roles.OPERATOR,
  ],
};

export const DATETIME_PICKERS_VIEWS: DateOrTimeView[] | undefined = [
  "year",
  "month",
  "day",
  "hours",
  "minutes",
  "seconds",
];

export enum MovementTypes {
  Default = 1,
  Income = 1,
  Discharge = 2,
  Additional = 3,
  Preparation = 4,
  Transfer = 5,
  IncomeDraft = 6,
  DischargeDraft = 7,
  PreparationDraft = 8,
  IncomeAdjustment = 9,
  DischargeAdjustment = 10,
  WorkOrder = 11,
}

export enum Order {
  Ascending = "asc",
  Descending = "desc",
}

export const MOVEMENT_TYPES = {
  DEFAULT: { id: MovementTypes.Default, label: "" },
  INCOME: { id: MovementTypes.Income, label: "ingreso" },
  DISCHARGE: { id: MovementTypes.Discharge, label: "egreso" },
  ADDITIONAL: { id: MovementTypes.Additional, label: "adicional" },
  PREPARATION: { id: MovementTypes.Preparation, label: "preparación" },
  TRANSFER: { id: MovementTypes.Transfer, label: "transferencia" },
  INCOME_DRAFT: { id: MovementTypes.IncomeDraft, label: "ingreso (borrador)" },
  DISCHARGE_DRAFT: {
    id: MovementTypes.DischargeDraft,
    label: "egreso (borrador)",
  },
  PREPARATION_DRAFT: {
    id: MovementTypes.PreparationDraft,
    label: "preparación (borrador)",
  },
  INCOME_ADJUSTMENT: {
    id: MovementTypes.IncomeAdjustment,
    label: "ingreso por ajuste de inventario",
  },
  DISCHARGE_ADJUSTMENT: {
    id: MovementTypes.DischargeAdjustment,
    label: "egreso por ajuste de inventario",
  },
  WORK_ORDER: { id: MovementTypes.WorkOrder, label: "orden de trabajo" },
};

export const defaultMovement: Movement = {
  movement_type_id: MovementTypes.Default,
  date: "",
  client_id: 0,
  associated_client: "",
  client_document: "",
  numero_remito: "",
  pallets: 0,
  observation: "",
  created_by: 0,
  updated_by: 0,
  is_billable: false,
  store_id: null,
  weight: 0,
  articles: [],
  additional_items: [],
  containers: [],
  isAdjustment: false,
  movement_id: null,
};

export const DATE_VIEWS = ["year", "month", "day"] as const;

export const ORDER_RESERVATION_STRATEGIES = {
  KEEP: "keep",
  OPTIMIZE: "optimize",
} as const;

export const ORDER_STATUSES = {
  INGRESADO: "ingresado",
  EN_PREPARACION: "en_preparacion",
  PREPARADO: "preparado",
  DESPACHADO: "despachado",
  ENTREGADO: "entregado",
  DETENIDO: "detenido",
  CANCELADO: "cancelado",
} as const;

export const ORDER_SOURCES = {
  MANUAL: "manual",
  IMPORT: "import",
} as const;

export const SETTLEMENT_TYPES = {
  ABONO: "abono",
  PALLETS: "pallets",
  UNIDADES: "unidades",
  PESO: "peso",
  PESO_VOLUMETRICO: "peso_volumetrico",
  VOLUMEN: "volumen",
  SUPERFICIE: "superficie",
};

export const SETTLEMENT_TYPE_NAMES = {
  [SETTLEMENT_TYPES.ABONO]: "Abono",
  [SETTLEMENT_TYPES.PALLETS]: "Pallets",
  [SETTLEMENT_TYPES.UNIDADES]: "Unidades",
  [SETTLEMENT_TYPES.PESO]: "Peso",
  [SETTLEMENT_TYPES.PESO_VOLUMETRICO]: "Peso volumétrico",
  [SETTLEMENT_TYPES.VOLUMEN]: "Volúmen",
  [SETTLEMENT_TYPES.SUPERFICIE]: "Superficie",
};

export const SETTLEMENT_TYPE_CATEGORIES = {
  INCOME: "INGRESO",
  STAY: "ESTADIA",
  DISCHARGE: "EGRESO",
};

export const defaultEditArticleValue: Article = {
  id: 0,
  code: "",
  client: {
    id: 0,
    razon_social: "",
    code: "",
    address: "",
    cuit: "",
    contact: "",
    observations: "",
    is_active: false,
  },
  client_id: 0,
  container: {
    id: 0,
    description: "",
    m3_min: 0,
    m3_max: 0,
    volume_weight: 0,
    is_active: false,
  },
  container_id: 0,
  description: "",
  weight: 0,
  width: null,
  height: null,
  long: null,
  amount: 0,
  amount_per_container: 0,
  unit: {
    id: 0,
    description: "",
    is_active: false,
  },
  unit_id: 0,
  presentation: null,
  fce: 1,
};

export const EXCEL_SHEETS = {
  CABECERA: "Cabecera",
  ARTICULOS: "Articulos",
  ADICIONALES: "Items Adicionales",
  ORDERS_IMPORT: "Pedidos",
  PREPICKING: "PrePicking",
};

export const EXCEL_HEADERS = {
  CABECERA: {
    REMITO: "Remito",
    FECHA: "Fecha",
    CLIENTE: "Cliente",
    CLIENTE_ASOCIADO: "Cliente Asociado",
    DOCUMENTO_DEL_CLIENTE: "Documento del Cliente",
    CREATED_BY: "Usuario",
    UPDATED_BY: "Preparó",
    CODIGO_ALMACEN: "Almacen",
    OBSERVACIONES: "Observaciones",
    PALLETS: "Pallets",
    PESO: "Peso",
  },
  ARTICULOS: {
    REMITO: "Remito",
    CODIGO_ARTICULO: "Articulo",
    CANTIDAD: "Cantidad",
    MERCADERIA_DISPONIBLE: "Mercaderia Disponible",
    LOTE: "Lote",
    FECHA_VENCIMIENTO: "Fecha de Vencimiento",
    FECHA_INGRESO: "Fecha de Ingreso",
    ORDEN_DE_COMPRA: "Orden de Compra",
    CANTIDAD_POR_CONTENEDOR: "Cantidad por contenedor",
    UBICACION: "Ubicacion",
    SUBTOTAL: "Subtotal",
  },
  ADICIONALES: {
    REMITO: "Remito",
    DESCRIPCION: "Descripcion",
    CANTIDAD: "Cantidad",
    CODIGO_LINEA: "Codigo Linea",
  },
  ORDERS_IMPORT: {
    FECHA: "Fecha",
    CLIENTE: "Cliente",
    DOCUMENTO_DEL_CLIENTE: "Documento del Cliente",
    CLIENTE_ASOCIADO: "Cliente Asociado",
    OBSERVACIONES: "Observaciones",
    ARTICULO: "Articulo",
    CANTIDAD: "Cantidad",
    LOTE: "Lote",
    ORDEN_DE_COMPRA: "OC",
    FECHA_VENCIMIENTO: "Fecha Vencimiento",
    VEHICULO: "Vehiculo",
  },
  PREPICKING: {
    ARTICULO: "Articulo",
    CANTIDAD: "Cantidad",
    FECHA_INGRESO: "Fecha Ingreso",
    LOTE: "Lote",
    ORDEN_DE_COMPRA: "Orden de Compra",
    FECHA_DE_VENCIMIENTO: "Fecha de Vencimiento",
  },
};

export const ARGENTINA_TIME_ZONE = "America/Argentina/Buenos_Aires";
export const DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const DATE_FORMAT = "DD/MM/YYYY";

export const ENTRY_MOVEMENT_TYPES = [1, 9];

export const EXIT_MOVEMENT_TYPES = [
  MovementTypes.Discharge,
  MovementTypes.Preparation,
  MovementTypes.DischargeAdjustment,
];
